import {
  Badge,
  Button,
  ButtonProps,
  Flex,
  IconButton,
} from '@utilitywarehouse/hearth-react';
import { CopySmallIcon } from '@utilitywarehouse/hearth-react-icons';
import { useCallback, useEffect, useRef, useState } from 'react';

import './styles.scss';

interface CopyButtonProps {
  value: string;
  label?: string;
  onCopy: (value: string) => void;
  buttonProps: ButtonProps;
  iconButton?: boolean;
}

type Timer = ReturnType<typeof setTimeout>;

const CopyButton = ({
  value,
  label = 'Copy link',
  iconButton = false,
  onCopy,
  buttonProps,
}: CopyButtonProps) => {
  const button = useRef<HTMLButtonElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const hasClipboard = navigator.clipboard;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipTimeout, setTooltipTimeout] = useState<Timer>();
  const tooltipAnimDuration = 1500;

  const handleCopy = useCallback(async () => {
    // reset the tooltip and animation timeout
    // stops it getting stuck and allows it to display
    // on every click
    clearTimeout(tooltipTimeout);
    setTooltipVisible(false);

    if (hasClipboard) {
      await navigator.clipboard.writeText(value);
    } else {
      // fallback if clipboard is unavailable
      input?.current?.select();
      document.execCommand('copy');
    }

    setTooltipVisible(true);
    const timeout = setTimeout(() => {
      setTooltipVisible(false);
    }, tooltipAnimDuration);
    setTooltipTimeout(timeout);

    onCopy(value);
  }, [value, onCopy, hasClipboard, tooltipTimeout]);

  useEffect(() => {
    const buttonElement = button.current;
    if (buttonElement) {
      buttonElement.addEventListener('click', handleCopy, true);
    }

    return () => buttonElement?.removeEventListener('click', handleCopy, true);
  }, [button, handleCopy]);

  return (
    <>
      <Flex
        position="relative"
        direction={{
          desktop: 'row',
          mobile: 'column',
        }}
      >
        {!iconButton && (
          <Button ref={button} className="copyButton__Button" {...buttonProps}>
            <CopySmallIcon /> {label}
          </Button>
        )}
        {iconButton && (
          <IconButton
            ref={button}
            label="Copy"
            className="copyButton__Button"
            {...buttonProps}
          >
            <CopySmallIcon />
          </IconButton>
        )}
        {tooltipVisible && (
          <Badge
            variant="subtle"
            size="sm"
            colorScheme="functional"
            className="copyButton__Tooltip"
          >
            Copied!
          </Badge>
        )}
      </Flex>

      {!hasClipboard && (
        <input
          type="text"
          value={value}
          readOnly
          ref={input}
          style={{ position: 'absolute', left: -9000 }}
        />
      )}
    </>
  );
};

export { CopyButton };
